/**
 * NotificationBell Component
 *
 * Renders a bell icon in the header with unread notification count badge.
 * Click opens a dropdown with recent notifications; "View all" links to the
 * full notification inbox page.
 *
 * API endpoints consumed (account-scoped, set by invitation-router.ts):
 *   GET  /api/clients/:accountId/notifications/unread-count
 *   GET  /api/clients/:accountId/notifications?limit=5
 *   POST /api/clients/:accountId/notifications/:id/read
 */

import { Component, For, Show, createSignal, onCleanup, onMount } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { useAuth } from "../store/authStore";
import { api } from "../utils/apiClient";
import { trace } from "../lib/trace";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Notification {
  id: string;
  accountId: string;
  type: "auto_join" | "access_request_auto" | "access_request_approved" | "access_request_denied";
  actorEmail: string;
  actorName?: string;
  roleAssigned: string;
  domain: string;
  message: string;
  read: boolean;
  createdAt: number;
  expiresAt: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const NOTIFICATION_TYPE_ICONS: Record<string, string> = {
  auto_join: "\u2022",
  access_request_auto: "\u26BF",
  access_request_approved: "\u2713",
  access_request_denied: "\u2717",
};

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const NotificationBell: Component = () => {
  const navigate = useNavigate();
  const { currentClient } = useAuth();
  const [showDropdown, setShowDropdown] = createSignal(false);
  const [unreadCount, setUnreadCount] = createSignal(0);
  const [notifications, setNotifications] = createSignal<Notification[]>([]);
  const [loading, setLoading] = createSignal(false);

  // ---- Fetch helpers ----

  const fetchUnreadCount = async () => {
    const accountId = currentClient()?.id;
    if (!accountId) return;
    try {
      const data = await api.get<{ unreadCount: number }>(
        `/api/clients/${encodeURIComponent(accountId)}/notifications/unread-count`,
      );
      setUnreadCount(data.unreadCount ?? 0);
    } catch (e: unknown) {
      trace.api("NotificationBell: unread count fetch error", { error: String(e) }, "error");
    }
  };

  const fetchRecentNotifications = async () => {
    const accountId = currentClient()?.id;
    if (!accountId) return;
    setLoading(true);
    try {
      const data = await api.get<{ notifications: Notification[]; total: number }>(
        `/api/clients/${encodeURIComponent(accountId)}/notifications?limit=5`,
      );
      setNotifications(data.notifications ?? []);
    } catch (e: unknown) {
      trace.api("NotificationBell: notification list fetch error", { error: String(e) }, "error");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notifId: string) => {
    const accountId = currentClient()?.id;
    if (!accountId) return;
    try {
      await api.post(
        `/api/clients/${encodeURIComponent(accountId)}/notifications/${encodeURIComponent(notifId)}/read`,
      );
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (e: unknown) {
      trace.api("NotificationBell: mark read error", { error: String(e) }, "error");
    }
  };

  // ---- Event handlers ----

  const handleBellClick = () => {
    const next = !showDropdown();
    setShowDropdown(next);
    if (next) {
      fetchRecentNotifications();
    }
  };

  const handleNotificationClick = (notif: Notification) => {
    if (!notif.read) {
      markAsRead(notif.id);
    }
  };

  const handleViewAll = () => {
    setShowDropdown(false);
    navigate("/notifications");
  };

  // Close dropdown when clicking outside
  const handleDocumentClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest("[data-notification-bell]")) {
      setShowDropdown(false);
    }
  };

  // ---- Lifecycle ----

  let pollTimer: ReturnType<typeof setInterval> | undefined;

  onMount(() => {
    trace.lifecycle("NotificationBell mounted");
    if (currentClient()) {
      fetchUnreadCount();
      pollTimer = setInterval(fetchUnreadCount, 60000);
    }
    document.addEventListener("click", handleDocumentClick);
  });

  onCleanup(() => {
    if (pollTimer) clearInterval(pollTimer);
    document.removeEventListener("click", handleDocumentClick);
  });

  // ---- Render ----

  return (
    <div class="nbell" data-notification-bell>
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        aria-label="Notifications"
        aria-expanded={showDropdown()}
        aria-haspopup="true"
        class="nbell__btn"
      >
        <svg aria-hidden="true"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--text-primary)"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        <Show when={unreadCount() > 0}>
          <span class="nbell__badge">
            {unreadCount() > 99 ? "99+" : unreadCount()}
          </span>
        </Show>
      </button>

      {/* Dropdown */}
      <Show when={showDropdown()}>
        <div class="nbell__dropdown">
          {/* Header */}
          <div class="nbell__header">
            <span class="nbell__title">
              Notifications
            </span>
            <Show when={unreadCount() > 0}>
              <span class="nbell__unread-label">
                {unreadCount()} unread
              </span>
            </Show>
          </div>

          {/* Notification List */}
          <div class="nbell__list">
            <Show when={loading()}>
              <div class="nbell__empty">
                Loading...
              </div>
            </Show>
            <Show when={!loading() && notifications().length === 0}>
              <div class="nbell__empty nbell__empty--no-items">
                No notifications yet
              </div>
            </Show>
            <Show when={!loading() && notifications().length > 0}>
              <For each={notifications()}>
                {(notif) => (
                  <div
                    onClick={() => handleNotificationClick(notif)}
                    class={`nbell__item ${notif.read ? "nbell__item--read" : "nbell__item--unread"}`}
                  >
                    {/* Type icon */}
                    <span class="nbell__item-icon">
                      {NOTIFICATION_TYPE_ICONS[notif.type] ?? "\u2022"}
                    </span>
                    {/* Content */}
                    <div class="nbell__item-content">
                      <div
                        class={`nbell__item-message ${notif.read ? "" : "nbell__item-message--unread"}`}
                      >
                        {notif.message}
                      </div>
                      <div class="nbell__item-time">
                        {formatRelativeTime(notif.createdAt)}
                      </div>
                    </div>
                    {/* Unread indicator */}
                    <Show when={!notif.read}>
                      <span class="nbell__unread-dot" />
                    </Show>
                  </div>
                )}
              </For>
            </Show>
          </div>

          {/* Footer — View All */}
          <div class="nbell__footer">
            <button
              onClick={handleViewAll}
              class="nbell__view-all"
            >
              View all notifications
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
};
